import {
  listBenderVerificationIssues,
  type BenderVerificationIssue,
  type BenderVerificationSummary,
} from '@509-bender/bender-library';

export interface BenderDataQualitySummary {
  label: string;
  caption: string;
  issueCountLabel: string;
  issueLabels: string[];
  hasFieldReferences: boolean;
  blockerCount: number;
}

export function formatVerificationSummary(summary: BenderVerificationSummary): string {
  const base = `${summary.sourcedFieldCount}/${summary.totalFieldCount} constants sourced`;
  const sources = summary.sourceCount === 1 ? '1 source' : `${summary.sourceCount} sources`;
  if (summary.missingFields.length === 0) return `${base} \u2022 ${sources}`;
  return `${base} \u2022 needs ${summary.missingFields.join(', ')} \u2022 ${sources}`;
}

export function getVerificationWarning(summary: BenderVerificationSummary): string | null {
  if (summary.status === 'verified') return null;
  if (summary.status === 'partial' && summary.missingFields.length > 0) {
    return `Some constants still need a manufacturer source: ${summary.missingFields.join(', ')}. Verify the shoe or manual before critical bends.`;
  }
  return 'Field-reference constants are not manufacturer-sourced. Verify the shoe or manual before bending.';
}

export function getBenderDataQualitySummary(
  issues: readonly BenderVerificationIssue[] = listBenderVerificationIssues(),
): BenderDataQualitySummary {
  if (issues.length === 0) {
    return {
      label: 'Manufacturer sourced',
      caption: 'All shipped bender constants are matched to manufacturer sources.',
      issueCountLabel: 'No bender data checks remaining',
      issueLabels: [],
      hasFieldReferences: false,
      blockerCount: 0,
    };
  }

  const partialCount = issues.filter((issue) => issue.status === 'partial').length;
  const fieldReferenceCount = issues.filter((issue) => issue.status === 'field-reference').length;
  const parts = [
    formatIssueCount(partialCount, 'partially verified', 'partially verified'),
    formatIssueCount(fieldReferenceCount, 'field reference', 'field references'),
  ].filter((part): part is string => Boolean(part));

  return {
    label: parts.join(' \u00b7 '),
    caption:
      fieldReferenceCount > 0
        ? 'Verify field-reference benders against the shoe or manual before critical bends.'
        : 'Some constants still need a manufacturer source before production bends.',
    issueCountLabel: `${issues.length} bender data ${issues.length === 1 ? 'check' : 'checks'} remaining`,
    issueLabels: issues.map(formatIssueLabel),
    hasFieldReferences: fieldReferenceCount > 0,
    blockerCount: issues.length,
  };
}

function formatIssueLabel(issue: BenderVerificationIssue): string {
  return `${issue.manufacturer} ${issue.model}: needs ${issue.missingFields.join(', ')}`;
}

function formatIssueCount(count: number, singular: string, plural: string): string | null {
  if (count === 0) return null;
  return `${count} ${count === 1 ? singular : plural}`;
}
